import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { colors } from '@/constants/colors';
import { portalThemeStage, portalThemeText, type ThemeStage } from '@/utils/portalThemeProgress';

/**
 * Banner under the Portal Theme controls: saved → sending to the router →
 * live. Same shape as SsidRenameProgress: derived from record timestamps,
 * with the host's own Save instant standing in until the record reflects it.
 */
const RECORD_REFLECTS_SAVE_MS = 60_000;
type Device = Parameters<typeof portalThemeStage>[0];

const PortalThemeProgress: React.FC<{
   device: Device;
   savedAt?: number | null;
   onPoll?: () => void;
}> = ({ device, savedAt, onPoll }) => {
   const recordAt = device.portalConfigSavedAt ? new Date(device.portalConfigSavedAt).getTime() : 0;
   const recordReflectsSave = !!savedAt && recordAt >= savedAt - RECORD_REFLECTS_SAVE_MS;
   const anchorAt = savedAt && !recordReflectsSave ? savedAt : recordAt;
   const effective: Device =
      anchorAt === recordAt ? device : { ...device, portalConfigSavedAt: new Date(anchorAt) };

   const [now, setNow] = useState(() => Date.now());
   useEffect(() => {
      setNow(Date.now());
   }, [anchorAt]);

   const stage: ThemeStage = portalThemeStage(effective, Math.max(now, anchorAt));
   const active = stage === 'router-wait';

   useEffect(() => {
      if (!active) return;
      const tick = setInterval(() => setNow(Date.now()), 2000);
      return () => clearInterval(tick);
   }, [active]);
   const pollRef = useRef(onPoll);
   pollRef.current = onPoll;
   useEffect(() => {
      if (!active) return;
      const poll = setInterval(() => pollRef.current?.(), 8_000);
      return () => clearInterval(poll);
   }, [active]);

   if (!stage) return null;
   return (
      <Banner data-stage={stage} $stage={stage}>
         {active && <Dot />}
         {portalThemeText(stage, Math.max(0, now - anchorAt))}
      </Banner>
   );
};

export default PortalThemeProgress;

const Banner = styled.div<{ $stage: ThemeStage }>`
   display: flex;
   align-items: center;
   gap: 8px;
   margin-top: 10px;
   padding: 9px 12px;
   border-radius: 8px;
   font-size: 12.5px;
   font-weight: 600;
   background: ${({ $stage }) =>
      $stage === 'done' ? `${colors.SUCCESS}18` : $stage === 'stuck' ? `${colors.DANGER}14` : `${colors.WARNING}1c`};
   color: ${({ $stage }) =>
      $stage === 'done' ? colors.SUCCESS : $stage === 'stuck' ? colors.DANGER : colors.WARNING};
   border: 1px solid currentColor;
`;
const Dot = styled.span`
   width: 8px;
   height: 8px;
   border-radius: 50%;
   background: currentColor;
   animation: sf-theme-blink 1s ease-in-out infinite;
   @keyframes sf-theme-blink {
      50% {
         opacity: 0.25;
      }
   }
`;
